<template>
  <div class="min-h-screen flex flex-col">
    <!-- Header -->
    <header
      :class="[
        'fixed top-0 left-0 w-full z-50 transition-all duration-300',
        solidHeader
          ? 'bg-td-navy/95 shadow-lg shadow-td-navy/10'
          : 'bg-transparent',
        navVisible ? 'translate-y-0' : '-translate-y-full',
      ]"
    >
      <div class="container mx-auto px-4 py-4">
        <div class="flex items-center justify-between">
          <!-- Logo -->
          <router-link to="/" class="flex items-center gap-3 group">
            <img src="@/assets/img/logonaam.png" alt="TEKDAM" class="h-10 w-auto brightness-0 invert" />
          </router-link>

          <!-- Desktop Navigation -->
          <nav class="hidden md:flex items-center gap-8">
            <router-link
              v-for="item in navigation"
              :key="item.href"
              :to="item.href"
              :class="[
                'text-sm font-semibold uppercase tracking-wider transition-colors duration-200',
                $route.path === item.href
                  ? 'text-td-blue-light'
                  : 'text-white/80 hover:text-white',
              ]"
            >
              {{ item.name }}
            </router-link>
            <div class="flex items-center rounded-lg border border-white/20 overflow-hidden">
              <button
                v-for="lang in locales"
                :key="lang"
                :class="[
                  'px-2.5 py-1.5 text-xs font-bold uppercase transition-colors',
                  locale === lang ? 'bg-td-blue text-white' : 'text-white/60 hover:text-white',
                ]"
                @click="setLocale(lang)"
              >
                {{ lang }}
              </button>
            </div>
            <router-link to="/contact" class="btn-primary text-xs py-2.5 px-6">
              {{ t.nav.getInTouch }}
            </router-link>
          </nav>

          <!-- Mobile: language switcher + menu button -->
          <div class="md:hidden flex items-center gap-3">
            <div class="flex items-center rounded-lg border border-white/20 overflow-hidden">
              <button
                v-for="lang in locales"
                :key="lang"
                :class="[
                  'px-2.5 py-1.5 text-xs font-bold uppercase transition-colors',
                  locale === lang ? 'bg-td-blue text-white' : 'text-white/60 hover:text-white',
                ]"
                @click="setLocale(lang)"
              >
                {{ lang }}
              </button>
            </div>
          <button class="text-white p-2" @click="mobileMenuOpen = !mobileMenuOpen">
            <svg v-if="!mobileMenuOpen" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          </div>
        </div>
      </div>
    </header>

    <!-- Mobile drawer overlay -->
    <Transition name="mobile-fade">
      <div v-if="mobileMenuOpen" class="fixed inset-0 z-40 bg-black/60" @click="mobileMenuOpen = false"></div>
    </Transition>
    <Transition name="mobile-slide">
      <div v-if="mobileMenuOpen" class="fixed top-0 left-0 z-50 h-full w-72 bg-td-navy shadow-2xl flex flex-col">
        <div class="flex items-center justify-between px-5 pt-6 pb-4 border-b border-white/10">
          <img src="@/assets/img/logo.png" alt="TEKDAM" class="h-8 w-auto brightness-0 invert" />
          <button class="text-white p-1" @click="mobileMenuOpen = false">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <nav class="flex-1 overflow-y-auto px-5 py-6 space-y-1">
          <router-link
            v-for="item in navigation"
            :key="item.href"
            :to="item.href"
            :class="[
              'block px-4 py-3 rounded-xl text-sm font-semibold uppercase tracking-wider transition-colors',
              $route.path === item.href
                ? 'bg-td-blue/20 text-td-blue-light'
                : 'text-white/70 hover:bg-white/5 hover:text-white',
            ]"
            @click="mobileMenuOpen = false"
          >
            {{ item.name }}
          </router-link>
        </nav>

        <div class="px-5 pb-6 space-y-3 border-t border-white/10 pt-4">
          <router-link
            to="/contact"
            class="block text-center w-full btn-primary"
            @click="mobileMenuOpen = false"
          >
            {{ t.nav.getInTouch }}
          </router-link>
        </div>
      </div>
    </Transition>

    <!-- Main content -->
    <main class="flex-1">
      <router-view />
    </main>

    <!-- Footer -->
    <footer class="gradient-navy text-white relative overflow-hidden">
      <!-- Decorative elements -->
      <div class="absolute top-0 right-0 w-96 h-96 rounded-full glow-orb-5 -translate-y-1/2 translate-x-1/3"></div>
      <div class="absolute bottom-0 left-0 w-64 h-64 rounded-full glow-orb-5 translate-y-1/2 -translate-x-1/3"></div>

      <div class="container mx-auto px-4 pt-16 pb-10 relative z-10">
        <div class="grid md:grid-cols-3 gap-10">
          <!-- Brand -->
          <div>
            <img src="@/assets/img/logonaam.png" alt="TEKDAM" class="h-10 w-auto brightness-0 invert mb-4" />
            <p class="text-white/60 text-sm leading-relaxed max-w-xs">
              {{ t.footer.description }}
            </p>
          </div>

          <!-- Quick Links -->
          <div>
            <h4 class="text-sm font-bold uppercase tracking-wider text-white/40 mb-4">{{ t.footer.navigation }}</h4>
            <nav class="flex flex-col gap-2">
              <router-link
                v-for="link in navigation"
                :key="link.href"
                :to="link.href"
                class="text-sm text-white/60 hover:text-td-blue-light transition-colors"
              >
                {{ link.name }}
              </router-link>
            </nav>
          </div>

          <!-- Contact Info -->
          <div class="md:justify-self-end md:text-right">
            <h4 class="text-sm font-bold uppercase tracking-wider text-white/40 mb-4">{{ t.footer.contact }}</h4>
            <div class="space-y-2 text-sm text-white/60">
              <p class="font-semibold text-white">TEKDAM BV</p>
              <p>BTW: BE1024.973.363</p>
              <p>{{ t.footer.location }}</p>
              <p>
                <a href="mailto:info@tekdam.be" class="text-td-blue-light hover:text-white transition-colors">info@tekdam.be</a>
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom bar -->
      <div class="border-t border-white/10 relative z-10">
        <div class="container mx-auto px-4 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-white/40">
          <span>
            &copy; 2026 TEKDAM BV. {{ t.footer.rights }}
            <span class="mx-1.5">&middot;</span>
            <router-link to="/privacy" class="hover:text-td-blue-light transition-colors underline-offset-2 hover:underline">
              {{ t.footer.privacy }}
            </router-link>
          </span>
          <span>{{ t.footer.tagline }}</span>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from "vue";
import { useRoute } from "vue-router";
import { useI18n, type Locale } from "@/i18n";

const route = useRoute();
const { locale, t, setLocale } = useI18n();
const locales: Locale[] = ["nl", "en"];

const mobileMenuOpen = ref(false);
const navVisible = ref(true);
const scrolled = ref(false);
const lastScrollY = ref(0);

// The portfolio landing now has a light top section, so the transparent
// header (white logo/nav) would be invisible there — keep it solid.
const solidHeader = computed(() => scrolled.value || route.name === "portfolio");

watch(mobileMenuOpen, (open) => {
  document.body.style.overflow = open ? 'hidden' : '';
});

const onScroll = () => {
  const currentY = window.scrollY;
  navVisible.value = currentY < lastScrollY.value || currentY < 50;
  scrolled.value = currentY > 20;
  lastScrollY.value = currentY;
};

onMounted(() => {
  window.addEventListener("scroll", onScroll, { passive: true });
});

onUnmounted(() => {
  window.removeEventListener("scroll", onScroll);
});

const navigation = computed(() => [
  { name: t.value.nav.home, href: "/" },
  { name: t.value.nav.portfolio, href: "/portfolio" },
  { name: t.value.nav.whoAreWe, href: "/who-are-we" },
  { name: t.value.nav.contact, href: "/contact" },
]);
</script>
